import Banner from "@components/features/product-list/banners/Banner";
import Link from "next/link";
import { useRouter } from "next/router";
import Breadcrumb from "@components/atoms/Breadcrumb";
import SubcategoriesSelected from "@components/features/product-list/filters/SubcategoriesSelected";
import SubcategoriesFilter from "@components/features/product-list/filters/SubcategoriesFilter";
import PriceFilter from "@components/features/product-list/filters/PriceFilter";
import BrandsFilter from "@components/features/product-list/filters/BrandsFilter";
import CateProducts from "@components/features/product-list/lists/CateProducts";
import {
  ProductAttributeFilter,
  ProductFilterLayer,
  ProductListMode,
  ProductSort,
  ProductSortBy,
} from "@interfaces/components/product-filters.interface";
import {
  LAYOUT_DEFAULT,
  PRODUCT_DEFAULT_PAGE,
  PRODUCT_DEFAULT_SORT_BY,
  PRODUCT_PAGE_SIZE,
  PRODUCT_SORT_LIST,
} from "@const/products.const";
import {
  useAttributes,
  useBrandRouteMapping,
  useCategoryHeader,
  useCategoryRouteMapping,
} from "@api/products/categories";
import Loader from "@components/atoms/Loader";
import _ from "underscore";
import { useEffect, useState } from "react";
import { RangeValues } from "@interfaces/components/range-slide.interface";
import { Attribute, CategoryHeader } from "@interfaces/dto/categories.dto";
import { useBrandProducts, useProducts } from "@api/products/products";
import { Product } from "@interfaces/dto/products.dto";
import {
  DelimitedNumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";
import { extractRangeValuesFromString } from "@utils/numbers.util";
import { composeArrayToQueryParam } from "@utils/query.utils";
import { isWebview, showMenuBarNative } from "@utils/webview.util";
// SEO
import { NextSeo } from "next-seo";
import {
  convertKeywordstoArray,
  convertRobotstoBoolean,
} from "@utils/seo.util";
import { useSeoBrandDetail } from "@api/seo/seo-page";
// banner header
import { useBrandHeader } from "@api/products/brands";
import { SEO_SITEURL } from "@const/seo.const";
import CmsDetail from "@components/features/cms-page/CmsDetail";

const getProductAttributeFilters = ({
  attrs,
  layer,
}: {
  attrs: Attribute | undefined;
  layer: ProductFilterLayer;
}): ProductAttributeFilter[] => {
  const filteredAttrs = attrs?.items.find(
    ({ attr_code }) => attr_code === layer
  )?.values;
  return filteredAttrs ? filteredAttrs : [];
};

const getSelectedAttrs = ({
  selected,
  productAttrFilters,
}: {
  selected: number[];
  productAttrFilters: ProductAttributeFilter[];
}): ProductAttributeFilter[] => {
  const selectedProductAttrFiltered = productAttrFilters
    ? productAttrFilters.filter((subcat) =>
        selected.some((item) => item === subcat.value)
      )
    : [];
  return selectedProductAttrFiltered;
};

function capitalizeFirstLetter(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const ProductsPage = () => {
  const router = useRouter();
  // set attribute states
  const [selectedAttrSubcats, setSelectedAttrSubcats] = useQueryParam(
    "cat",
    withDefault(DelimitedNumericArrayParam, [])
  );
  const [selectedPriceRange, setSelectedPriceRange] = useQueryParam(
    "price",
    withDefault(StringParam, undefined)
  );
  const [selectedAttrBrands, setSelectedAttrBrands] = useQueryParam(
    "brand",
    withDefault(DelimitedNumericArrayParam, [])
  );

  // set paging states
  const [selectedPage, setSelectedPage] =
    useState<number>(PRODUCT_DEFAULT_PAGE);
  const [selectedSortBy, setSelectedSortBy] = useState<ProductSortBy>(
    PRODUCT_DEFAULT_SORT_BY
  );

  // set products render states
  const [selectedProductListMode, setSelectedProductListMode] = useQueryParam(
    "product_list_mode",
    StringParam
  );
  const [renderedProducts, setRenderedProducts] = useState<Product[]>([]);
  const [listKey, setListKey] = useState<number>(0); // generate incremental list key to refresh component
  const [orgPriceFilter, setOrgPriceFilter] = useState<RangeValues>();
  const [categoryTitle, setCategoryTitle] = useState<string>("");

  // get category mapping ID
  const { name } = router.query;

  // get brand url value
  const {
    data: dataBrandId,
    isLoading: isLoadingBrandId,
    error: errorCatId,
  } = useBrandRouteMapping({
    urlPath: name ? (name as string) : undefined,
  });

  // get attributes
  const { data: dataAttr, isLoading: isLoadingAttr } = useAttributes({
    categoryId: "2",
    subcatIds: selectedAttrSubcats as number[],
    priceRange: selectedPriceRange,
    brandIds: !isLoadingBrandId ? [parseInt(dataBrandId!.id)] : [],
  });

  // console.log(dataAttr);
  // [subcat]
  const attrSubcats = getProductAttributeFilters({
    attrs: dataAttr,
    layer: "cat",
  });
  // [brand]
  const attrBrands = getProductAttributeFilters({
    attrs: dataAttr,
    layer: "brand",
  });
  // [display subcat]
  const displaySubcats = getSelectedAttrs({
    selected: selectedAttrSubcats as number[],
    productAttrFilters: attrSubcats,
  });
  // [price]
  const attrPrice = getProductAttributeFilters({
    attrs: dataAttr,
    layer: "price",
  });

  // get banner header
  const { data: dataBrandHeader, isLoading: isLoadingBrandHeader } =
    useBrandHeader(dataBrandId?.id);

  // get products
  const {
    data: dataProducts,
    isLoading: isLoadingProducts,
    error: errorProducts,
    mutate: mutateProducts,
  } = useBrandProducts({
    priceRange: selectedPriceRange,
    brandId: !isLoadingBrandId ? parseInt(dataBrandId!.id) : 0,
    productDisplayConfig: {
      pageSize: PRODUCT_PAGE_SIZE,
      currentPage: selectedPage,
      sortBy:
        selectedSortBy === "price_desc" || selectedSortBy === "price_asc"
          ? "price"
          : selectedSortBy,
      soryDirection:
        selectedSortBy === "price_desc" || selectedSortBy === "price_asc"
          ? (PRODUCT_SORT_LIST.find((sort) => sort.value === selectedSortBy)
              ?.desc as ProductSort)
          : undefined,
    },
  });

  // handle set org price range
  // useEffect(() => {
  //   // init org price
  //   if (attrPrice.length > 0 && !orgPriceFilter) {
  //     const rangeValue = extractRangeValuesFromString(
  //       attrPrice[0].value.toString(),
  //     );
  //     setOrgPriceFilter({
  //       start: Number(rangeValue?.start),
  //       end: Number(rangeValue?.end),
  //     });
  //   }
  // }, [attrPrice, orgPriceFilter]);

  // handle append products
  useEffect(() => {
    if (dataProducts?.items) {
      if (selectedPage === 1) {
        setRenderedProducts(dataProducts.items);
      } else {
        setRenderedProducts((prev) => [...prev, ...dataProducts.items]);
      }
    }
  }, [dataProducts?.items, selectedPage]);

  // handle invalid category
  // if (errorCatId) {
  //   router.push('/404');
  //   return;
  // }

  // handle invalid products
  // if (errorProducts) {
  //   // TODO: handle when error from products reponse
  // }

  // handle clear filters
  // const handleClearFilters = () => {
  //   window.location.replace(
  //     `/${composeArrayToQueryParam(categories as string[])}`,
  //   );
  // };
  const handleClearFilters = () => {
    window.location.replace(`/brands/${name}`);
  };

  // handle product list mode
  const handleProductListMode = (mode: ProductListMode) => {
    setSelectedProductListMode(mode);
  };

  // handle reset page
  const handleResetPage = () => {
    setListKey((prev) => prev + 1);
    setSelectedPage(PRODUCT_DEFAULT_PAGE);
  };

  // handle subcat filter
  const handleSubcatFilter = (subcatId: number, isChecked: boolean) => {
    setSelectedAttrSubcats((prev) => {
      const prevSubcatFilters = prev as number[];
      if (isChecked) {
        return _.sortBy(_.uniq([...prevSubcatFilters, subcatId]));
      } else {
        return prevSubcatFilters.filter((subcat) => subcat !== subcatId);
      }
    });
    handleResetPage();
  };

  // handle price filter
  const handlePriceFilter = ({ start, end }: RangeValues) => {
    setSelectedPriceRange(`${start}-${end}`);
    handleResetPage();
  };

  // handle brand filter
  const handleBrandFilter = (brandId: number, isChecked: boolean) => {
    setSelectedAttrBrands((prev) => {
      const prevBrandFilters = prev as number[];
      if (isChecked) {
        return _.sortBy(_.uniq([...prevBrandFilters, brandId]));
      } else {
        return prevBrandFilters.filter((brand) => brand !== brandId);
      }
    });
    handleResetPage();
  };

  // handle sortby filter
  const handleSortByFilter = (sortBy: ProductSortBy) => {
    setSelectedSortBy(sortBy);
    handleResetPage();
  };

  // handle paging filter
  const handlePageFilter = (page: number) => {
    setSelectedPage(page);
  };

  // get SEO
  const { data: metaData, isLoading: isLoadingMetaData } = useSeoBrandDetail(
    dataBrandId?.id as string
  );
  const [metaDataState, setMetaDataState] = useState(metaData);
  if (metaData && !metaDataState) {
    setMetaDataState(metaData);
  }
  const { noIndex, noFollow } = convertRobotstoBoolean(
    metaDataState?.robots as string
  );
  const keywords = convertKeywordstoArray(metaDataState?.keywords as string);

  useEffect(() => {
    if (isWebview()) {
      showMenuBarNative();
    }
  }, []);

  // render loading
  // let isLoadingBrandId = false;
  if (isLoadingBrandId) {
    return (
      <section className="mainContent">
        <div className="container-fluid">
          <Loader />
        </div>
      </section>
    );
  }

  return (
    <>
      {/* SEO */}
      {metaDataState && (
        <NextSeo
          title={metaDataState?.title}
          description={metaDataState?.description}
          additionalMetaTags={keywords}
          noindex={noIndex}
          nofollow={noFollow}
          openGraph={{
            type: "article",
            title: metaDataState?.title,
            description: metaDataState?.description,
            images: [
              {
                url: metaDataState?.image ?? "",
              },
            ],
          }}
          canonical={`${SEO_SITEURL}/brands/${name}`}
        />
      )}

      {/* Banner Header */}
      {dataBrandHeader?.small_image !== null && (
        <section className="banner">
          <div className="container-fluid">
            {isLoadingBrandHeader ? (
              <Loader />
            ) : (
              dataBrandHeader && <Banner url={dataBrandHeader.small_image} />
            )}
          </div>
        </section>
      )}

      {/* Main */}
      <section className="mainContent">
        <div className="container-fluid">
          <div className="row">
            <div className="col">
              <h1>{name?.toString().toUpperCase()}</h1>
              <div className="d-none d-lg-block">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link href={"/"}>หน้าแรก</Link>
                    </li>
                    <li className="breadcrumb-item active">
                      {capitalizeFirstLetter(name!.toString())}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="product-section">
        <div className="container-fluid">
          <div className="d-flex">
            <div className="sidebar-left filters offcanvas offcanvas-lg offcanvas-start">
              <a
                type="button"
                className="btn-close"
                data-bs-dismiss="offcanvas"
              >
                HIDE FILTER <i className="icon icon-close" />
              </a>
              {isLoadingAttr ? (
                <Loader />
              ) : (
                displaySubcats.length > 0 && (
                  <SubcategoriesSelected attributeFilters={displaySubcats} />
                )
              )}
              {isLoadingAttr ? (
                <Loader />
              ) : (
                attrSubcats.length > 0 && (
                  <SubcategoriesFilter
                    attributeFilters={attrSubcats}
                    selectedAttributeFilters={selectedAttrSubcats as number[]}
                    onFilterChange={handleSubcatFilter}
                  />
                )
              )}
              {orgPriceFilter && (
                <PriceFilter
                  orgPriceRange={orgPriceFilter}
                  defaultPriceRange={extractRangeValuesFromString(
                    selectedPriceRange
                  )}
                  onFilterChange={handlePriceFilter}
                />
              )}
              {isLoadingAttr ? (
                <Loader />
              ) : (
                attrBrands.length > 0 && (
                  <BrandsFilter
                    attributeFilters={attrBrands}
                    selectedAttributeFilter={selectedAttrBrands as number[]}
                    onFilterChange={handleBrandFilter}
                  />
                )
              )}
              <button
                onClick={handleClearFilters}
                className="btn btn-black w-100"
                disabled={
                  selectedAttrSubcats.length === 0 &&
                  !selectedPriceRange &&
                  selectedAttrBrands.length === 0
                }
              >
                ลบทั้งหมด
              </button>
            </div>
            {renderedProducts.length > 0 && (
              <CateProducts
                key={listKey}
                productListMode={
                  selectedProductListMode
                    ? (selectedProductListMode as ProductListMode)
                    : "grid"
                }
                onViewModeChange={handleProductListMode}
                products={renderedProducts}
                page={selectedPage}
                onPageChange={handlePageFilter}
                sortBy={selectedSortBy}
                onSortByChange={handleSortByFilter}
                allowLoadMore={
                  !isLoadingProducts &&
                  renderedProducts.length >= selectedPage * PRODUCT_PAGE_SIZE
                }
                pageLayout={LAYOUT_DEFAULT}
              />
            )}
          </div>

          {/* Bottom Content */}
          {metaData && (
            <div className="bg-white">
              <div className="container">
                <div className="py-3">
                  <CmsDetail cmsDescHtml={metaData?.page_description} />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ProductsPage;
